/* eslint-disable */
// Lens Development (Lens Lab) data access — React Query over lens_developments / lens_dev_events,
// plus thin wrappers for the 8 Package-B RPCs. RLS handles reads; every write goes through a gated
// SECURITY DEFINER RPC. Promote also invalidates the bank caches so the new lens shows up in Lens
// Bank + metrics without a manual refresh. Units-agnostic (this is dev metadata).
import { useQuery, useQueryClient } from '@tanstack/react-query';
import * as Sentry from '@sentry/react';
import { supabase } from '../../../../supabase';
import { fetchAllPaged } from '../../../../utils/pgIn';

export const LTD_KEY = ['ltd-list'];
export const STAGES = [
  { n: 1, label: 'Start development' },
  { n: 2, label: 'Round sample' },
  { n: 3, label: 'Full color code' },
  { n: 4, label: 'Laser cut lenses' },
  { n: 5, label: 'Lock & confirm' },
];
export const stageLabel = (n) => (STAGES.find((s) => s.n === n) || {}).label || '—';

const EXPECTED = new Set(['forbidden']);
const rpc = async (name, args) => {
  const { data, error } = await supabase.rpc(name, args);
  if (error) { try { Sentry.captureException(error, { tags: { area: 'lens-dev', op: name } }); } catch (_) {} throw error; }
  if (data && data.error) {
    if (!EXPECTED.has(data.error)) { try { Sentry.captureException(new Error(data.error), { tags: { area: 'lens-dev', op: name } }); } catch (_) {} }
    throw new Error(data.error);
  }
  return data;
};

export function useLtdList() {
  const q = useQuery({
    queryKey: LTD_KEY,
    // Paged: PostgREST caps a request at 1,000 rows and truncates SILENTLY (Conv #167). This table
    // is the single source for the grid AND every count beside it, so a truncated read would show a
    // confident total over a short grid. 723 rows today — one short page, i.e. one request, exactly
    // as before; it simply keeps telling the truth past 1,000.
    queryFn: () => fetchAllPaged(
      () => supabase.from('lens_developments').select('*').order('stage', { ascending: true }).order('updated_at', { ascending: false }),
      { label: 'useLtdList' },
    ),
    staleTime: 60000, retry: 0,
  });
  return { rows: q.data || [], isLoading: q.isLoading, error: q.error };
}

export function useLtdEvents(devId) {
  const q = useQuery({
    queryKey: ['ltd-events', devId],
    enabled: !!devId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('lens_dev_events').select('*').eq('dev_id', devId).order('created_at', { ascending: true });
      if (error) throw error;
      return data || [];
    },
    staleTime: 30000, retry: 0,
  });
  return { events: q.data || [], isLoading: q.isLoading };
}

// Central actions with the right invalidations. Consumers call these; no fetch logic in components.
export function useLtdActions() {
  const qc = useQueryClient();
  const inval = (id) => { qc.invalidateQueries({ queryKey: LTD_KEY }); if (id) qc.invalidateQueries({ queryKey: ['ltd-events', id] }); };
  const invalBank = () => { qc.invalidateQueries({ queryKey: ['lens-bank'] }); qc.invalidateQueries({ queryKey: ['lens-metrics'] }); qc.invalidateQueries({ queryKey: ['lens-link'] }); qc.invalidateQueries({ queryKey: ['lens-display'] }); };
  return {
    nextBarcode: (family) => rpc('next_lens_no', { p_family: family }),
    create:      async (payload)               => { const r = await rpc('ltd_create', { payload }); inval(); return r; },
    update:      async (id, payload)           => { const r = await rpc('ltd_update', { p_id: id, payload }); inval(id); return r; },
    setStage:    async (id, stage, note)       => { const r = await rpc('ltd_set_stage', { p_id: id, p_stage: stage, p_note: note ?? null }); inval(id); return r; },
    addNote:     async (id, stage, note, date) => { const r = await rpc('ltd_add_note', { p_id: id, p_stage: stage, p_note: note, p_note_date: date }); inval(id); return r; },
    cancel:      async (id, reason)            => { const r = await rpc('ltd_cancel', { p_id: id, p_reason: reason ?? null }); inval(id); return r; },
    reactivate:  async (id)                    => { const r = await rpc('ltd_reactivate', { p_id: id }); inval(id); return r; },
    duplicate:   async (id)                    => { const r = await rpc('ltd_duplicate_variant', { p_id: id }); inval(id); return r; },
    promote:     async (id)                    => { const r = await rpc('promote_development', { p_id: id }); inval(id); invalBank(); return r; },
    // P4 · the three RPCs of sql/2026-08-lens-ltd-and-promote.sql. Each DELEGATES server-side —
    // ltd_promote calls promote_development, peek calls next_lens_no — so no rule is duplicated here.
    peekBarcode: (familyId)                    => rpc('peek_next_lens_no', { p_family_id: familyId }),
    rename:      async (id, code)              => { const r = await rpc('ltd_rename', { p_dev_id: id, p_new_code: code }); inval(id); invalBank(); return r; },
    promoteWithFamily: async (id, familyId)    => { const r = await rpc('ltd_promote', { p_dev_id: id, p_family_id: familyId ?? null }); inval(id); invalBank(); return r; },
  };
}

// Derived helpers shared across the board + cards.
// Ready to lock: a colour family is set. Material approval remains editable development metadata,
// but it does not block moving a lens into the bank.
// ONE plain-words mapping for every lens RPC failure the user can hit, imported by BOTH the
// barcode-preview path (LtdModal) and the promote path (LtdPromoteDialog) — Convention #61: the rule
// exists once, so the two surfaces cannot drift into saying different things about the same error.
// Gate E-2 of docs/audit/lens-portal-redesign-discovery.md: the preview's bare .catch() discarded the
// error entirely, so `Lock & promote` sat disabled beside "— — —" with nothing on screen to explain it.
// `forbidden` is the one worth naming precisely — it is Convention #89 in the field: an app-side
// `lens` portal grant is NOT the DB-side state has_lens_admin() reads, so canWrite renders the whole
// cluster while every RPC refuses.
export function lensErrText(e) {
  const raw = String((e && e.message) || e || '')
    .replace(/^cannot promote:\s*/i, '')
    .replace(/^(ltd_rename|ltd_promote|peek_next_lens_no|promote_development):\s*/i, '')
    .trim();
  const m = raw.toLowerCase();
  if (m === 'forbidden' || m.startsWith('forbidden -')) return 'You do not have permission to write to the Lens Bank — a lens editor or admin must do this.';
  if (m.includes('color family is not set')) return 'Missing: colour family. Choose one above, then lock.';
  if (m.includes('unknown lens family')) return 'This colour family has no barcode prefix configured, so no barcode can be allocated.';
  if (m.includes('lens_bank_lens_no_key')) return 'The next barcode is already used in the bank — the allocator is out of step with the bank.';
  // ltd_create answers these as an ENVELOPE ({error:...}), not an exception — rpc() rethrows the
  // string, so they arrive here looking like any other message. P4.
  if (m === 'bad_ltd_code') return 'That is not a valid LTD code. Use LTD + a number, optionally a letter and a dash suffix — e.g. LTD410 or LTD258B-4.';
  if (m === 'ltd_code_taken') return 'That LTD code is already taken.';
  if (m.includes('tr') && m.includes('approv')) return 'Missing: TR material approval.';
  if (m.includes('pc') && m.includes('approv')) return 'Missing: PC material approval.';
  return raw || 'The server refused the request without giving a reason.';
}

export const isReady = (d) => !!(d && d.family_id);
export const daysSince = (iso) => {
  if (!iso) return 0;
  const then = Date.parse(iso); if (isNaN(then)) return 0;
  return Math.max(0, Math.floor((Date.now() - then) / 86400000));
};
